import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    List,
    ListItem,
    Collapse,
    IconButton,
    Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export interface WokeDoneItem {
    id: string;
    code: string | null;
    symbol: string | null;
    content: string;
    parentId: string | null;
}

export interface WokeDoneData {
    code: string;
    wd: WokeDoneItem[];
    dynamicWdMaps: Record<string, string[]>;
}

export interface WokeDoneDictionaryPaperProps {
    data?: WokeDoneData | null;
    embed?: boolean;
}

const WokeDoneItemRow: React.FC<{ item: WokeDoneItem }> = ({ item }) => {
    const decodeSymbol = (str: string | null) => {
        if (!str) return '';
        try {
            return JSON.parse(`"${str}"`);
        } catch {
            return str;
        }
    };

    const symbol = decodeSymbol(item.symbol);
    const symbolDisplay = symbol || '';
    const content = item.content;

    return (
        <ListItem sx={{ py: 0.1, alignItems: 'flex-start', pl: 1.5, pr: 0 }} disableGutters>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <Typography
                    variant="caption"
                    sx={{
                        width: 36,
                        flexShrink: 0,
                        fontFamily: 'monospace',
                        color: 'text.secondary',
                    }}
                >
                    {symbolDisplay}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        flex: 1,
                        lineHeight: 1.4,
                        color: 'text.secondary',
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {content}
                </Typography>
            </Box>
        </ListItem>
    );
};

const WokeDoneDictionaryPaper: React.FC<WokeDoneDictionaryPaperProps> = ({ data, embed }) => {
    const [expanded, setExpanded] = useState(false);

    if (!data) return null;
    const hasWd = data.wd && data.wd.length > 0;
    const hasDynamic = data.dynamicWdMaps && Object.keys(data.dynamicWdMaps).length > 0;
    if (!hasWd && !hasDynamic) return null;

    const dynamicEntries = hasDynamic ? Object.entries(data.dynamicWdMaps) : [];

    // embed 模式：仅渲染内容区域，不包含 Paper 外壳与标题栏
    const contentBody = (
        <Box sx={{ px: 2, py: 0.5, maxHeight: 400, overflow: 'auto' }}>
            {hasWd && (
                <List dense disablePadding>
                    {data.wd.map((item) => (
                        <WokeDoneItemRow key={item.id} item={item} />
                    ))}
                </List>
            )}

            {hasDynamic && (
                <>
                    {hasWd && <Divider sx={{ my: 1.5 }} />}
                    {dynamicEntries.map(([groupKey, items], idx) => (
                        <React.Fragment key={groupKey}>
                            {idx > 0 && (
                                <Divider sx={{ my: 1.5, ml: 2, width: `calc(100% - 16px)` }} />
                            )}
                            <Box sx={{ mb: 1.5 }}>
                                <List dense disablePadding>
                                    {items.map((text, itemIdx) => (
                                        <ListItem
                                            key={itemIdx}
                                            sx={{ py: 0.1, pl: 1.5 }}
                                            disableGutters
                                        >
                                            <Box sx={{ display: 'flex', width: '100%' }}>
                                                <Typography variant="caption" sx={{ width: 36, flexShrink: 0, color: 'text.secondary' }} />
                                                <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.4, color: 'text.secondary' }}>
                                                    {text}
                                                </Typography>
                                            </Box>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </React.Fragment>
                    ))}
                </>
            )}
        </Box>
    );

    if (embed) {
        return contentBody;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                mt: 2,
                p: 0,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 0.75,
                    bgcolor: 'action.hover',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'action.selected' },
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleOutlineIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600 }}>
                        WOKE DONE
                    </Typography>
                </Box>
                <IconButton size="small">
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                {contentBody}
            </Collapse>
        </Paper>
    );
};

export default WokeDoneDictionaryPaper;